import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, TrendingUp, TrendingDown, DollarSign, Activity, ChevronDown, ChevronLeft, ChevronRight, Calendar, X } from 'lucide-react';
import marginApi from '../../api/marginApi';
import formatCurrency from '../../utils/formatCurrency';
import { MarginV2Response, MarginWeek, Transaction, TransactionFilterParams } from '../../api/types';
import WeeklyDrilldownModal from './components/WeeklyDrilldownModal';
import ChatPanel from '../chat/ChatPanel';
import ChatButton from '../../components/ChatButton';
import { useIsMobile } from '../../utils/hooks';
import { useUser } from '../../context/UserContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import userMetadataApi from '../../api/userMetadataApi';
import SearchableBusinessSelect from '../../components/SearchableBusinessSelect';
import { addDays, currentMonthBounds, formatDateKey, formatMonthKey, startOfWeekMonday } from '../../utils/tutorialDates';
import { isTutorialRuntimeActive } from '../../components/tutorial/tutorialRuntime';

const parseLocalDate = (value: string): Date => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, (month || 1) - 1, day || 1);
};

const MONTH_ABBR = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const toMonthKey = (year: number, month: number): string => `${year}-${String(month).padStart(2, '0')}`;

const formatMonthLabel = (year: number, month: number): string => {
    if (month < 1 || month > 12) return `${year}-${String(month).padStart(2, '0')}`;
    return `${MONTH_ABBR[month - 1]} ${year}`;
};

const formatPeriodLabel = (start?: string, end?: string): string => {
    if (!start && !end) return '—';
    if (!start) return end || '—';
    if (!end) return start;

    const startDate = parseLocalDate(start);
    const endDate = parseLocalDate(end);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return `${start} - ${end}`;
    }

    const mmdd = (date: Date) => `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
    return `${mmdd(startDate)} - ${mmdd(endDate)}`;
};

const buildMarginTutorialData = (today: Date = new Date()): MarginV2Response => {
    const { monthStart, monthEnd, previousMonthStart, previousMonthEnd } = currentMonthBounds(today);
    const currentWeek = startOfWeekMonday(today);
    const weeks = [-2, -1, 0, 1].map((offset) => {
        const start = addDays(currentWeek, offset * 7);
        return { start, end: addDays(start, 6) };
    });

    return {
        _id: 'tutorial-margin',
        business_id: 'tutorial-business',
        month: today.getMonth() + 1,
        year: today.getFullYear(),
        updated_at: today.toISOString(),
        period_start: formatDateKey(monthStart),
        period_end: formatDateKey(monthEnd),
        four_week_avg: {
            revenue: 24990.33,
            cogs: 3646.12,
            labor: 14857.79,
            opex: 2382.53,
            not_categorized: 678.24,
            gross_profit: 21344.21,
            net_profit: 18183.44,
            margin_percent: 0.252,
            gross_margin_percent: 0.854,
        },
        weeks: [
            { week_start: formatDateKey(weeks[0].start), week_end: formatDateKey(weeks[0].end), revenue: 31120.37, cogs: 8489.22, labor: 18739.99, opex: 1525.40, not_categorized: 780.86, gross_profit: 22631.15, net_profit: 2366.36, margin_percent: 0.076, gross_margin_percent: 0.727 },
            { week_start: formatDateKey(weeks[1].start), week_end: formatDateKey(weeks[1].end), revenue: 33245.23, cogs: 961.76, labor: 19686.03, opex: 1656.12, not_categorized: 23.89, gross_profit: 32283.47, net_profit: 10941.32, margin_percent: 0.329, gross_margin_percent: 0.971 },
            { week_start: formatDateKey(weeks[2].start), week_end: formatDateKey(weeks[2].end), revenue: 28181.42, cogs: 5133.48, labor: 14388.12, opex: 1619.85, not_categorized: 710.75, gross_profit: 23047.94, net_profit: 7039.97, margin_percent: 0.250, gross_margin_percent: 0.818 },
            { week_start: formatDateKey(weeks[3].start), week_end: formatDateKey(weeks[3].end), revenue: 0, cogs: 0, labor: 0, opex: 0, not_categorized: 0, gross_profit: 0, net_profit: 0, margin_percent: 0, gross_margin_percent: 0 },
        ],
        mtd_metrics: {
            current: {
                revenue: 99961.31,
                cogs: 14584.46,
                labor: 59431.66,
                opex: 9530.12,
                not_categorized: 2712.68,
                gross_profit: 85376.85,
                net_profit: 16415.07,
                gross_margin_percent: 0.854,
                margin_percent: 0.164,
            },
            previous: {
                revenue: 176310.25,
                cogs: 31011.22,
                labor: 68040.14,
                opex: 33439.02,
                not_categorized: 1880.43,
                gross_profit: 154102.04,
                net_profit: 39827.15,
                gross_margin_percent: 0.873,
                margin_percent: 0.226,
            },
            changes: {
                revenue: -43.3,
                gross_profit: -44.6,
                opex: -71.5,
                operating_profit: -58.8,
                gross_margin_pts: -1.9,
            },
            ranges: {
                current_start: formatDateKey(monthStart),
                current_end: formatDateKey(monthEnd),
                prev_start: formatDateKey(previousMonthStart),
                prev_end: formatDateKey(previousMonthEnd),
            },
        },
        available_months: [{ year: today.getFullYear(), month: today.getMonth() + 1, value: formatMonthKey(today), label: formatMonthLabel(today.getFullYear(), today.getMonth() + 1) }],
    };
};

const buildMarginTutorialTransactions = (today: Date = new Date()): Transaction[] => [
    {
        _id: 'tutorial-margin-txn-1',
        date: formatDateKey(addDays(today, -2)),
        vendor_name: 'Vendor A',
        description: 'Demo subscription charge',
        original_description: 'DEMO SUBSCRIPTION CHARGE',
        currency: 'USD',
        amount: -3.25,
        finz_account: 'Other',
        fiscal_bucket: 'Other',
        category: 'Other',
        needs_review: true,
        confidence_score: 0,
        operating_flag: true,
        source: 'tutorial',
        user_id: 'tutorial',
        direction: 'outflow',
        resolution_status: 'needs_review',
    },
    {
        _id: 'tutorial-margin-txn-2',
        date: formatDateKey(addDays(today, -4)),
        vendor_name: 'Vendor B',
        description: 'Demo payroll payment',
        original_description: 'DEMO PAYROLL PAYMENT',
        currency: 'USD',
        amount: -186.94,
        finz_account: 'Labor',
        fiscal_bucket: 'Labor',
        category: 'Labor',
        needs_review: true,
        confidence_score: 0,
        operating_flag: true,
        source: 'tutorial',
        user_id: 'tutorial',
        direction: 'outflow',
        resolution_status: 'needs_review',
    },
    {
        _id: 'tutorial-margin-txn-3',
        date: formatDateKey(addDays(today, -5)),
        vendor_name: 'Vendor C',
        description: 'Demo uncategorized debit',
        original_description: 'DEMO UNCATEGORIZED DEBIT',
        currency: 'USD',
        amount: -331.20,
        finz_account: 'Uncategorized',
        fiscal_bucket: null,
        category: null,
        needs_review: true,
        confidence_score: 0,
        operating_flag: true,
        source: 'tutorial',
        user_id: 'tutorial',
        direction: 'outflow',
        resolution_status: 'needs_review',
    },
];

const MarginV2: React.FC = () => {
    const { isAdmin } = useUser();
    const { workspaces, activeBusinessId, setActiveBusinessId, isLoading: isWorkspaceLoading, isFetching: isWorkspaceFetching, isError: isWorkspaceError, refetch: refetchWorkspace } = useWorkspace();
    const queryClient = useQueryClient();
    const isMobile = useIsMobile();

    // State
    const currentDate = new Date();
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth()); // 0-indexed
    const [showChat, setShowChat] = useState(!isMobile);
    const [extendChat, setExtendChat] = useState(false);
    const [showSetupGuidePanel, setShowSetupGuidePanel] = useState(false);
    const [showMonthFilter, setShowMonthFilter] = useState(false);
    const [business, setBusiness] = useState('Select Business');
    const [selectedBusinessId, setSelectedBusinessId] = useState<string | undefined>(undefined);
    const [drilldownModalOpen, setDrilldownModalOpen] = useState(false);
    const [drilldownState, setDrilldownState] = useState<{
        title: string;
        start: string;
        end: string;
        amount: number;
        filters: Partial<TransactionFilterParams>;
    } | null>(null);
    const [isTutorialDemo, setIsTutorialDemo] = useState(() => isTutorialRuntimeActive('margin'));

    useEffect(() => {
        if (isMobile) {
            setShowChat(false);
            setExtendChat(false);
        }
    }, [isMobile]);

    useEffect(() => {
        const handleSetupGuideVisibility = (event: Event) => {
            setShowSetupGuidePanel(Boolean((event as CustomEvent<{ open?: boolean }>).detail?.open));
        };

        setShowSetupGuidePanel(document.body.classList.contains('finz-setup-guide-open'));
        window.addEventListener('finz:setup-guide:visibility', handleSetupGuideVisibility);
        return () => window.removeEventListener('finz:setup-guide:visibility', handleSetupGuideVisibility);
    }, []);
    const desktopMonthDropdownRef = useRef<HTMLDivElement | null>(null);
    const mobileMonthDropdownRef = useRef<HTMLDivElement | null>(null);
    const { data: allBusinessesData } = useQuery({
        queryKey: ['allBusinesses'],
        queryFn: userMetadataApi.getAllBusinesses,
        enabled: !!isAdmin,
    });

    const userBusinessOptions = Array.from(
        new Map(
            workspaces.flatMap((workspace) =>
                (workspace.businesses || []).map((business) => [
                    business.id,
                    {
                        id: business.id,
                        name: business.legal_name || business.dba_name || 'Unnamed Business',
                    },
                ]),
            ),
        ).values(),
    );
    const adminBusinessOptions = allBusinessesData?.businesses.map((b) => ({
        id: b.business_id || b.user_id,
        name: b.legal_name || b.dba_name || b.onboarding?.qbo_metadata?.company_name || b.user_email || 'Unknown',
        subtitle: b.user_email || undefined,
    })) || [];
    const businessOptions = isAdmin ? adminBusinessOptions : userBusinessOptions;

    useEffect(() => {
        if (activeBusinessId) {
            setSelectedBusinessId(activeBusinessId);
            const biz = businessOptions.find(b => b.id === activeBusinessId);
            if (biz) setBusiness(biz.name);
        }
    }, [activeBusinessId, businessOptions]);
    const effectiveBusinessId = selectedBusinessId || activeBusinessId || undefined;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                !desktopMonthDropdownRef.current?.contains(target)
                && !mobileMonthDropdownRef.current?.contains(target)
            ) {
                setShowMonthFilter(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleShowChat = () => {
        setShowChat(true);
    };

    const handleDrilldownClick = (
        title: string,
        start: string,
        end: string,
        amount: number,
        filters: Partial<TransactionFilterParams>
    ) => {
        setDrilldownState({ title, start, end, amount, filters });
        setDrilldownModalOpen(true);
    };

    const marginTutorialData = useMemo(() => buildMarginTutorialData(), []);
    const marginTutorialTransactions = useMemo(() => buildMarginTutorialTransactions(), []);
    const { data, isLoading, error } = useQuery<MarginV2Response>({
        queryKey: ['margin-v2', selectedYear, selectedMonth, effectiveBusinessId],
        queryFn: () => marginApi.getMarginV2(selectedYear, selectedMonth + 1, effectiveBusinessId), // API expects 1-indexed month
        enabled: !!effectiveBusinessId,
    });
    const displayData = isTutorialDemo ? marginTutorialData : data;

    useEffect(() => {
        const handleTutorialStep = (event: Event) => {
            const detail = (event as CustomEvent).detail as { pageKey?: string } | undefined;
            if (detail?.pageKey === 'margin') setIsTutorialDemo(true);
        };
        const handleTutorialActive = (event: Event) => {
            const detail = (event as CustomEvent<{ pageKey?: string; active?: boolean }>).detail;
            setIsTutorialDemo(detail?.active !== false && detail?.pageKey === 'margin');
        };
        const openUncategorizedTutorialDrilldown = () => {
            setIsTutorialDemo(true);
            const week = marginTutorialData.weeks.find((item) => (item.not_categorized || 0) !== 0) || marginTutorialData.weeks[0];
            if (!week) return;
            handleDrilldownClick(
                'Uncategorized transactions',
                week.week_start,
                week.week_end,
                week.not_categorized || 0,
                { resolution_status: ['needs_review'] }
            );
        };
        window.addEventListener('finz:tutorial:margin-open-uncategorized', openUncategorizedTutorialDrilldown);
        const closeTutorialDrilldown = (event: Event) => {
            const detail = (event as CustomEvent).detail as { pageKey?: string } | undefined;
            if (detail?.pageKey !== 'margin') return;
            setIsTutorialDemo(false);
            setDrilldownModalOpen(false);
        };
        if (isTutorialRuntimeActive('margin')) setIsTutorialDemo(true);
        window.addEventListener('finz:tutorial:active', handleTutorialActive);
        window.addEventListener('finz:tutorial:step', handleTutorialStep);
        window.addEventListener('finz:tutorial:closed', closeTutorialDrilldown);
        return () => {
            window.removeEventListener('finz:tutorial:active', handleTutorialActive);
            window.removeEventListener('finz:tutorial:step', handleTutorialStep);
            window.removeEventListener('finz:tutorial:margin-open-uncategorized', openUncategorizedTutorialDrilldown);
            window.removeEventListener('finz:tutorial:closed', closeTutorialDrilldown);
        };
    }, []);

    const weeklySummedTotals = useMemo(() => {
        const weeks = displayData?.weeks || [];
        const totals = weeks.reduce<MarginWeek>(
            (acc, week) => {
                acc.revenue += week.revenue || 0;
                acc.cogs += week.cogs || 0;
                acc.labor += week.labor || 0;
                acc.opex += week.opex || 0;
                acc.not_categorized = (acc.not_categorized || 0) + (week.not_categorized || 0);
                acc.gross_profit += week.gross_profit || 0;
                acc.net_profit += week.net_profit || 0;
                return acc;
            },
            {
                week_start: '',
                week_end: '',
                revenue: 0,
                cogs: 0,
                labor: 0,
                opex: 0,
                not_categorized: 0,
                gross_profit: 0,
                net_profit: 0,
                margin_percent: 0,
            }
        );

        totals.margin_percent = totals.revenue !== 0 ? totals.net_profit / totals.revenue : 0;
        return totals;
    }, [displayData?.weeks]);

    const monthlyCurrentMetrics = displayData?.mtd_metrics?.current;

    const weekToDateMetrics = useMemo(() => {
        const weeks = displayData?.weeks || [];
        if (weeks.length === 0) return null;

        const now = new Date();
        const isCurrentMonth = selectedYear === now.getFullYear() && selectedMonth === now.getMonth();
        const asOfDate = isCurrentMonth
            ? new Date(now.getFullYear(), now.getMonth(), now.getDate())
            : new Date(selectedYear, selectedMonth + 1, 0);

        const matchingWeek = weeks.find((week) => {
            const start = parseLocalDate(week.week_start);
            const end = parseLocalDate(week.week_end);
            return start <= asOfDate && asOfDate <= end;
        });

        return matchingWeek || weeks[weeks.length - 1] || null;
    }, [displayData?.weeks, selectedMonth, selectedYear]);

    const monthlyTotals = useMemo(() => {
        const current = monthlyCurrentMetrics;
        if (!current) return weeklySummedTotals;

        const revenue = Number(current.revenue || 0);
        const cogs = Number(current.cogs || 0);
        const labor = Number(current.labor || 0);
        const opex = Number(current.opex || 0);
        const notCategorized = Number(current.not_categorized || 0);
        const grossProfit = Number(current.gross_profit || 0);
        const netProfit = Number(current.net_profit || 0);
        const marginPercent = revenue !== 0 ? netProfit / revenue : Number(current.margin_percent || 0);

        return {
            week_start: '',
            week_end: '',
            revenue,
            cogs,
            labor,
            opex,
            not_categorized: notCategorized,
            gross_profit: grossProfit,
            net_profit: netProfit,
            margin_percent: marginPercent,
        } as MarginWeek;
    }, [monthlyCurrentMetrics, weeklySummedTotals]);

    const monthlyPeriodLabel = useMemo(() => {
        const weeks = displayData?.weeks || [];
        if (weeks.length === 0) return '—';

        const monthStart = new Date(selectedYear, selectedMonth, 1);
        const monthEnd = new Date(selectedYear, selectedMonth + 1, 0);
        const toLocalDateString = (value: Date) =>
            `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;

        return formatPeriodLabel(toLocalDateString(monthStart), toLocalDateString(monthEnd));
    }, [displayData?.weeks, selectedMonth, selectedYear]);

    const syncMutation = useMutation({
        mutationFn: () => marginApi.syncMarginV2(selectedYear, selectedMonth + 1, effectiveBusinessId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['margin-v2'] });
        },
    });

    const handleSync = () => {
        syncMutation.mutate();
    };

    const selectedMonthKey = toMonthKey(selectedYear, selectedMonth + 1);
    const monthOptionsMap = new Map<string, { year: number; month: number; label: string }>();

    (displayData?.available_months || []).forEach((option) => {
        if (!option || typeof option.year !== 'number' || typeof option.month !== 'number') {
            return;
        }
        if (option.month < 1 || option.month > 12) {
            return;
        }
        const key = toMonthKey(option.year, option.month);
        monthOptionsMap.set(key, {
            year: option.year,
            month: option.month,
            label: option.label || formatMonthLabel(option.year, option.month),
        });
    });

    const availableMonthOptions = Array.from(monthOptionsMap.values()).sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
    });
    const hasAvailableMonths = availableMonthOptions.length > 0;
    const monthOptions = hasAvailableMonths
        ? availableMonthOptions
        : [{
            year: selectedYear,
            month: selectedMonth + 1,
            label: formatMonthLabel(selectedYear, selectedMonth + 1),
        }];
    const selectedExistsInAvailableMonths = availableMonthOptions.some(
        (option) => option.year === selectedYear && option.month === selectedMonth + 1
    );
    const firstAvailableMonth = availableMonthOptions[0];

    useEffect(() => {
        if (!hasAvailableMonths) return;
        if (selectedExistsInAvailableMonths) return;
        if (!firstAvailableMonth) return;

        setSelectedYear(firstAvailableMonth.year);
        setSelectedMonth(firstAvailableMonth.month - 1);
    }, [
        hasAvailableMonths,
        selectedExistsInAvailableMonths,
        firstAvailableMonth,
    ]);

    if (error && !isTutorialDemo) {
        return (
            <div className="p-8 text-center">
                <div className="text-red-500 mb-2">Error loading margin data</div>
                <button
                    onClick={() => window.location.reload()}
                    className="text-indigo-600 hover:underline"
                >
                    Retry
                </button>
            </div>
        );
    }

    // Wait for workspace/business before fetching margin data
    if (!effectiveBusinessId && !isTutorialDemo) {
        const handleRetry = () => {
            queryClient.invalidateQueries({ queryKey: ['workspaceContext'] });
        };
        return (
            <div className="flex h-full items-center justify-center">
                <div className="flex flex-col items-center gap-4 text-center max-w-md px-4">
                    {isWorkspaceLoading || isWorkspaceFetching ? (
                        <>
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
                            <p className="text-gray-500 text-sm">
                                {isWorkspaceFetching && isWorkspaceError ? 'Retrying...' : 'Loading workspace...'}
                            </p>
                        </>
                    ) : isWorkspaceError ? (
                        <>
                            <p className="text-gray-600 text-sm">Failed to load workspace. Please try again.</p>
                            <button
                                type="button"
                                onClick={handleRetry}
                                disabled={isWorkspaceFetching}
                                className="px-4 py-2 rounded-lg bg-indigo-500 text-white text-sm font-medium hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Retry
                            </button>
                        </>
                    ) : (
                        <p className="text-gray-500 text-sm">
                            {isAdmin ? 'Select a business to view margin data.' : 'Setting up your workspace...'}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full overflow-hidden relative">
            <div className={`overflow-auto flex-1 ${showChat && !showSetupGuidePanel && !isMobile ? 'w-[70%]' : 'w-full'}`}>
                <div className="min-w-0 mx-5 mb-5 mt-[16px] space-y-6">
                    {/* Header */}
                    <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="flex items-center justify-between gap-3">
                            <h1 className="pt-0.5 text-2xl font-semibold">Margin</h1>
                            <div className="flex items-center gap-2 sm:hidden">
                                <div
                                    ref={mobileMonthDropdownRef}
                                    className="relative flex h-10 items-center rounded-md border border-gray-300 bg-white px-3 shadow-sm"
                                >
                                    <button
                                        type="button"
                                        className="flex h-full items-center justify-between gap-2 text-sm leading-none text-gray-700 focus:outline-none"
                                        onClick={() => setShowMonthFilter((prev) => !prev)}
                                    >
                                        <Calendar size={15} className="shrink-0 text-gray-500" />
                                        <span className="max-w-[64px] truncate">
                                            {monthOptions.find((option) => toMonthKey(option.year, option.month) === selectedMonthKey)?.label || formatMonthLabel(selectedYear, selectedMonth + 1)}
                                        </span>
                                        <ChevronDown size={15} className="shrink-0 text-gray-500" />
                                    </button>

                                    {showMonthFilter && (
                                        <div className="absolute right-0 top-[calc(100%+8px)] z-50 max-h-64 w-[180px] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                                            {monthOptions.map((option) => {
                                                const optionKey = toMonthKey(option.year, option.month);
                                                const isSelected = optionKey === selectedMonthKey;
                                                return (
                                                    <button
                                                        key={optionKey}
                                                        type="button"
                                                        className={`w-full px-3 py-2 text-left text-sm transition-colors hover:bg-gray-100 focus:outline-none ${isSelected ? 'bg-gray-50 font-semibold text-gray-900' : 'text-gray-700'}`}
                                                        onClick={() => {
                                                            setSelectedYear(option.year);
                                                            setSelectedMonth(option.month - 1);
                                                            setShowMonthFilter(false);
                                                        }}
                                                    >
                                                        {option.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={handleSync}
                                    disabled={syncMutation.isPending}
                                    aria-label="Refresh"
                                    title="Refresh"
                                    className={`relative flex h-10 w-10 items-center justify-center rounded border text-sm shadow-sm ${syncMutation.isPending ? 'bg-gray-300 text-gray-700' : 'bg-indigo-500 text-white hover:bg-indigo-600'}`}
                                >
                                    <RefreshCw size={16} className={syncMutation.isPending ? 'animate-spin' : ''} />
                                </button>
                            </div>
                        </div>
                        <div className="flex w-full flex-wrap items-stretch justify-end gap-3 lg:w-auto lg:items-start">
                            <div
                                ref={desktopMonthDropdownRef}
                                className="relative hidden h-[44px] w-full rounded-md border border-gray-300 bg-white px-4 py-1.5 shadow-sm sm:block sm:w-[180px]"
                            >
                                <button
                                    type="button"
                                    className="w-full h-full flex items-center justify-between gap-2 text-sm text-gray-700 leading-none focus:outline-none"
                                    onClick={() => setShowMonthFilter((prev) => !prev)}
                                >
                                    <span className="inline-flex items-center gap-2 truncate">
                                        <Calendar size={16} className="text-gray-500 shrink-0" />
                                        <span className="truncate pl-1">
                                            {monthOptions.find((option) => toMonthKey(option.year, option.month) === selectedMonthKey)?.label || formatMonthLabel(selectedYear, selectedMonth + 1)}
                                        </span>
                                    </span>
                                    <ChevronDown size={16} className="text-gray-500 shrink-0" />
                                </button>

                                {showMonthFilter && (
                                    <div className="absolute top-[calc(100%+8px)] right-0 w-[180px] bg-white border border-gray-200 rounded-md shadow-lg z-50 overflow-hidden">
                                        <div className="max-h-64 overflow-y-auto">
                                            {monthOptions.map((option) => {
                                                const optionKey = toMonthKey(option.year, option.month);
                                                const isSelected = optionKey === selectedMonthKey;
                                                return (
                                                    <button
                                                        key={optionKey}
                                                        type="button"
                                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:outline-none transition-colors ${isSelected ? 'font-semibold text-gray-900 bg-gray-50' : 'text-gray-700'}`}
                                                        onClick={() => {
                                                            setSelectedYear(option.year);
                                                            setSelectedMonth(option.month - 1);
                                                            setShowMonthFilter(false);
                                                        }}
                                                    >
                                                        {option.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {businessOptions.length > 1 && (
                                <SearchableBusinessSelect
                                    options={businessOptions}
                                    value={activeBusinessId || selectedBusinessId}
                                    onChange={(id) => {
                                        const option = businessOptions.find(b => b.id === id);
                                        setBusiness(option?.name || 'Select Business');
                                        setSelectedBusinessId(id);
                                        setActiveBusinessId(id);
                                    }}
                                    widthClassName="w-full sm:w-80"
                                    showSubtitleWhenClosed={false}
                                />
                            )}

                            <button
                                onClick={handleSync}
                                disabled={syncMutation.isPending}
                                aria-label="Refresh"
                                title="Refresh"
                                className={`relative hidden h-11 w-10 items-center justify-center rounded border text-sm shadow-sm sm:flex sm:w-auto sm:px-6 ${syncMutation.isPending ? 'bg-gray-300 text-gray-700' : 'bg-indigo-500 text-white hover:bg-indigo-600'}`}
                            >
                                <span>{syncMutation.isPending ? 'Syncing...' : 'Refresh'}</span>
                            </button>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="flex justify-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                        </div>
                    ) : !displayData ? (
                        <div className="text-center py-12 text-gray-500">No data available for this period.</div>
                    ) : (
                        <>
                            {/* MTD Metrics Row */}
                            <div data-tutorial="margin-metrics" className="space-y-4">
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                    <div className="col-span-2 md:col-span-1">
                                        <SummaryCard
                                            tutorialAnchor="margin-metric-primary"
                                            title="Total Revenue (MTD)"
                                            value={displayData.mtd_metrics?.current.revenue}
                                            trend={displayData.mtd_metrics?.changes.revenue}
                                            isTrendPercentage
                                        />
                                    </div>
                                    <SummaryCard
                                        title="Gross Profit (MTD)"
                                        value={displayData.mtd_metrics?.current.gross_profit}
                                        trend={displayData.mtd_metrics?.changes.gross_profit}
                                        isTrendPercentage
                                    />
                                    <SummaryCard
                                        title="Gross Margin (MTD)"
                                        value={displayData.mtd_metrics?.current.gross_margin_percent ? displayData.mtd_metrics.current.gross_margin_percent * 100 : 0}
                                        suffix="%"
                                        isPercentage
                                        trend={displayData.mtd_metrics?.changes.gross_margin_pts}
                                        isTrendPercentage={false} // Points change
                                        trendSuffix=" pts"
                                    />
                                    <SummaryCard
                                        title="OpEx (MTD)"
                                        value={displayData.mtd_metrics?.current.opex}
                                        trend={displayData.mtd_metrics?.changes.opex}
                                        inverseTrend
                                        isTrendPercentage
                                    />
                                    <SummaryCard
                                        title="Operating Profit (MTD)"
                                        value={displayData.mtd_metrics?.current.net_profit}
                                        trend={displayData.mtd_metrics?.changes.operating_profit}
                                        isTrendPercentage
                                    />
                                </div>
                            </div>

                            {/* Weekly Average Metrics Row */}
                            <div data-tutorial="margin-metrics" className="space-y-4">
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                    <div className="col-span-2 md:col-span-1">
                                        <SummaryCard
                                            tutorialAnchor="margin-metric-wtd-primary"
                                            title="Total Revenue (WTD)"
                                            value={weekToDateMetrics?.revenue ?? 0}
                                        />
                                    </div>

                                    <SummaryCard
                                        title="Gross Profit (WTD)"
                                        value={weekToDateMetrics?.gross_profit ?? 0}
                                    />
                                    <SummaryCard
                                        title="Gross Margin (WTD)"
                                        value={weekToDateMetrics?.gross_margin_percent ? weekToDateMetrics.gross_margin_percent * 100 : 0}
                                        suffix="%"
                                        isPercentage
                                    />
                                    <SummaryCard
                                        title="OpEx (WTD)"
                                        value={weekToDateMetrics?.opex ?? 0}
                                    />
                                    <SummaryCard
                                        title="Operating Profit (WTD)"
                                        value={weekToDateMetrics?.net_profit ?? 0}
                                    />
                                </div>
                            </div>

                            {/* Weeks Table */}
                            <div data-tutorial="margin-weekly-table" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                <div className="overflow-x-auto px-2">
                                    <table className="w-full text-left border-collapse [&_th:first-child]:pl-4 [&_th:last-child]:pr-5 [&_td:first-child]:pl-4 [&_td:last-child]:pr-5">
                                        <thead>
                                            <tr className="border-b border-gray-200 text-gray-500">
                                                <th className="text-[#4C545F] p-4 whitespace-nowrap font-inter font-medium text-[14px] leading-[120%] w-[170px]">Period</th>
                                                <th className="text-[#4C545F] p-4 text-right whitespace-nowrap font-inter font-medium text-[14px] leading-[120%] align-middle">Revenue</th>
                                                <th className="text-[#4C545F] p-4 text-right whitespace-nowrap font-inter font-medium text-[14px] leading-[120%] align-middle">COGS</th>
                                                <th className="text-[#4C545F] p-4 text-right whitespace-nowrap font-inter font-medium text-[14px] leading-[120%] align-middle">Labor</th>
                                                <th className="text-[#4C545F] p-4 text-right whitespace-nowrap font-inter font-medium text-[14px] leading-[120%] align-middle">OpEx</th>
                                                <th data-tutorial="margin-uncategorized-column" className="text-[#4C545F] p-4 text-right whitespace-nowrap font-inter font-medium text-[14px] leading-[120%] align-middle">Uncategorized</th>
                                                <th className="text-[#4C545F] p-4 text-right whitespace-nowrap font-inter font-medium text-[14px] leading-[120%] align-middle">Gross Profit</th>
                                                <th className="text-[#4C545F] p-4 text-right whitespace-nowrap font-inter font-medium text-[14px] leading-[120%] align-middle">Profit</th>
                                                <th className="text-[#4C545F] p-4 text-right whitespace-nowrap font-inter font-medium text-[14px] leading-[120%] align-middle">Margin %</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {displayData.weeks?.map((week, index) => (
                                                <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                    <td className="p-4 text-sm text-gray-900 whitespace-nowrap">
                                                        {parseLocalDate(week.week_start).toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' })} - {parseLocalDate(week.week_end).toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' })}
                                                    </td>
                                                    {week.revenue && week.revenue !== 0 ? (
                                                        <td
                                                            className="p-4 text-sm text-right text-indigo-600 cursor-pointer hover:underline whitespace-nowrap"
                                                            onClick={() => handleDrilldownClick(
                                                                'Revenue Transactions',
                                                                week.week_start,
                                                                week.week_end,
                                                                week.revenue,
                                                                { fiscal_bucket: ['Revenue'], resolution_status: ["verified", "awaiting_approval"] }
                                                            )}
                                                        >
                                                            {formatCurrency(week.revenue)}
                                                        </td>
                                                    ) : (
                                                        <td className="p-4 text-sm text-right text-gray-400 whitespace-nowrap">-</td>
                                                    )}
                                                    {week.cogs && week.cogs !== 0 ? (
                                                        <td
                                                            className="p-4 text-sm text-right text-indigo-600 cursor-pointer hover:underline whitespace-nowrap"
                                                            onClick={() => handleDrilldownClick(
                                                                'COGS Transactions',
                                                                week.week_start,
                                                                week.week_end,
                                                                week.cogs,
                                                                { fiscal_bucket: ['COGS'], resolution_status: ["verified", "awaiting_approval"] }
                                                            )}
                                                        >
                                                            {formatCurrency(week.cogs)}
                                                        </td>
                                                    ) : (
                                                        <td className="p-4 text-sm text-right text-gray-400 whitespace-nowrap">-</td>
                                                    )}
                                                    {week.labor && week.labor !== 0 ? (
                                                        <td
                                                            className="p-4 text-sm text-right text-indigo-600 cursor-pointer hover:underline whitespace-nowrap"
                                                            onClick={() => handleDrilldownClick(
                                                                'Labor Transactions',
                                                                week.week_start,
                                                                week.week_end,
                                                                week.labor,
                                                                { fiscal_bucket: ['Labor'], resolution_status: ["verified", "awaiting_approval"] }
                                                            )}
                                                        >
                                                            {formatCurrency(week.labor)}
                                                        </td>
                                                    ) : (
                                                        <td className="p-4 text-sm text-right text-gray-400 whitespace-nowrap">-</td>
                                                    )}
                                                    {week.opex && week.opex !== 0 ? (
                                                        <td
                                                            className="p-4 text-sm text-right text-indigo-600 cursor-pointer hover:underline whitespace-nowrap"
                                                            onClick={() => handleDrilldownClick(
                                                                'OpEx Transactions',
                                                                week.week_start,
                                                                week.week_end,
                                                                week.opex,
                                                                { fiscal_bucket: ['Fixed Expense', 'Other Expense'], resolution_status: ["verified", "awaiting_approval"] }
                                                            )}
                                                        >
                                                            {formatCurrency(week.opex)}
                                                        </td>
                                                    ) : (
                                                        <td className="p-4 text-sm text-right text-gray-400 whitespace-nowrap">-</td>
                                                    )}
                                                    {week.not_categorized && week.not_categorized !== 0 ? (
                                                        <td
                                                            data-tutorial="margin-uncategorized-column"
                                                            className="p-4 text-sm text-right text-[#F66720] cursor-pointer hover:underline whitespace-nowrap"
                                                            onClick={() => handleDrilldownClick(
                                                                'Needs Review Transactions',
                                                                week.week_start,
                                                                week.week_end,
                                                                week.not_categorized || 0,
                                                                { resolution_status: ["needs_review"] }
                                                            )}
                                                        >
                                                            {formatCurrency(week.not_categorized || 0)}
                                                        </td>
                                                    ) : (
                                                        <td data-tutorial="margin-uncategorized-column" className="p-4 text-sm text-right text-gray-400 whitespace-nowrap">-</td>
                                                    )}
                                                    <td className="p-4 text-sm text-right text-[#4C545F] whitespace-nowrap">{formatCurrency(week.gross_profit)}</td>
                                                    <td className={`p-4 text-sm text-right text-[#4C545F] whitespace-nowrap`}>
                                                        {formatCurrency(week.net_profit)}
                                                    </td>
                                                    <td className={`p-4 text-sm text-right ${week.margin_percent >= 0 ? 'text-[#1A855C]' : 'text-[#B5213E]'} whitespace-nowrap`}>
                                                        {(week.margin_percent * 100).toFixed(1)}%
                                                    </td>
                                                </tr>
                                            ))}
                                            {(displayData.weeks?.length || 0) > 0 && (
                                                <tr>
                                                    <td className="p-4 pt-5 text-sm whitespace-nowrap border-t-2 border-gray-200">
                                                        {monthlyPeriodLabel}
                                                    </td>
                                                    <td className="p-4 pt-5 text-sm text-right whitespace-nowrap border-t-2 border-gray-200">
                                                        {formatCurrency(monthlyTotals.revenue)}
                                                    </td>
                                                    <td className="p-4 pt-5 text-sm text-right whitespace-nowrap border-t-2 border-gray-200">
                                                        {formatCurrency(monthlyTotals.cogs)}
                                                    </td>
                                                    <td className="p-4 pt-5 text-sm text-right whitespace-nowrap border-t-2 border-gray-200">
                                                        {formatCurrency(monthlyTotals.labor)}
                                                    </td>
                                                    <td className="p-4 pt-5 text-sm text-right whitespace-nowrap border-t-2 border-gray-200">
                                                        {formatCurrency(monthlyTotals.opex)}
                                                    </td>
                                                    <td data-tutorial="margin-uncategorized-column" className="p-4 pt-5 text-sm text-right whitespace-nowrap border-t-2 border-gray-200">
                                                        {formatCurrency(monthlyTotals.not_categorized || 0)}
                                                    </td>
                                                    <td className="p-4 pt-5 text-sm text-right whitespace-nowrap border-t-2 border-gray-200">
                                                        {formatCurrency(monthlyTotals.gross_profit)}
                                                    </td>
                                                    <td className="p-4 pt-5 text-sm text-right whitespace-nowrap border-t-2 border-gray-200">
                                                        {formatCurrency(monthlyTotals.net_profit)}
                                                    </td>
                                                    <td className={`p-4 pt-5 text-sm text-right whitespace-nowrap border-t-2 border-gray-200 ${monthlyTotals.margin_percent >= 0 ? 'text-[#1A855C]' : 'text-[#B5213E]'}`}>
                                                        {(monthlyTotals.margin_percent * 100).toFixed(1)}%
                                                    </td>
                                                </tr>
                                            )}
                                            {(!displayData.weeks || displayData.weeks.length === 0) && (
                                                <tr>
                                                    <td colSpan={9} className="p-8 text-center text-gray-500">
                                                        No weekly data available.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}

                    {drilldownState && (
                        <WeeklyDrilldownModal
                            isOpen={drilldownModalOpen}
                            onClose={() => setDrilldownModalOpen(false)}
                            title={drilldownState.title}
                            startDate={drilldownState.start}
                            endDate={drilldownState.end}
                            startAmount={drilldownState.amount}
                            initialFilters={drilldownState.filters}
                            businessId={effectiveBusinessId}
                            tutorialTransactions={isTutorialDemo ? marginTutorialTransactions : undefined}
                        />
                    )}
                </div>
            </div>

            {!isAdmin && showSetupGuidePanel && !isMobile && (
                <div
                    aria-hidden="true"
                    className="relative h-full w-[30%] max-w-[300px] min-w-[350px] shrink-0 border bg-white shadow-sm"
                />
            )}

            {/* Chat Panel */}
            {!isAdmin && <div className={`${showChat && !showSetupGuidePanel ? (isMobile ? 'fixed inset-0 z-[1999] flex bg-white w-full max-w-none h-[100dvh]' : 'flex bg-white') : 'hidden'} ${extendChat ? 'w-[40%] max-w-[600px]' : 'w-[30%] max-w-[300px]'} chat-panel`}>
                {/* Chat */}
                <div className="flex-1 overflow-auto flex flex-col">
                    <ChatPanel
                        page={'margin'}
                        year={selectedYear}
                        month={selectedMonth + 1}
                        initialPrompts={[
                            "Show gross margin percentage for this month",
                            "Compare profit change to last month",
                            "Show breakdown of OpEx for the last 4 weeks"
                        ]}
                        onClose={() => setShowChat(false)}
                    />
                </div>

                {/* Extend Button */}
                {!isMobile && (
                    <div onClick={() => setExtendChat(!extendChat)} className="border hover:bg-gray-200 hover:cursor-pointer py-5 rounded absolute bg-gray-100 top-20 -left-2">
                        {extendChat ? <ChevronRight size={18} className="text-gray-500" /> : <ChevronLeft size={18} className="text-gray-500" />}
                    </div>
                )}
            </div>}

            {/* Chat Toggle Button */}
            {(!showChat && !showSetupGuidePanel && !isAdmin) && <ChatButton handleShowChat={handleShowChat} />}
        </div>
    );
};

interface SummaryCardProps {
    title: string;
    value?: number;
    icon?: React.ReactNode;
    trend?: number;
    suffix?: string;
    isPercentage?: boolean;
    isTrendPercentage?: boolean;
    inverseTrend?: boolean; // If true, positive trend is bad (red)
    trendSuffix?: string;
    className?: string;
    tutorialAnchor?: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
    title,
    value,
    icon,
    trend,
    suffix = '',
    isPercentage = false,
    isTrendPercentage = false,
    inverseTrend = false,
    trendSuffix = '',
    className = '',
    tutorialAnchor
}) => {
    const formattedValue = value === undefined
        ? '-'
        : isPercentage
            ? `${value.toFixed(1)}${suffix}`
            : formatCurrency(value);

    // Trend logic
    const hasTrend = trend !== undefined && trend !== null;
    const isPositive = hasTrend && trend! > 0;
    const isNeutral = hasTrend && trend === 0;

    // Color logic: 
    // Default: Positive = Green, Negative = Red
    // Inverse: Positive = Red, Negative = Green
    let trendColor = 'text-gray-500';
    if (hasTrend && !isNeutral) {
        if (inverseTrend) {
            trendColor = isPositive ? 'text-[#B5213E]' : 'text-[#1A855C]';
        } else {
            trendColor = isPositive ? 'text-[#1A855C]' : 'text-[#B5213E]';
        }
    }

    const formattedTrend = hasTrend
        ? `${isPositive ? '+' : ''}${isTrendPercentage ? trend!.toFixed(1) + '%' : (trendSuffix ? trend!.toFixed(1) + trendSuffix : formatCurrency(trend!))}`
        : null;

    return (
        <div data-tutorial={tutorialAnchor} className={`bg-white p-5 rounded-xl shadow-sm border border-gray-200 ${className}`}>
            <div className="flex-col justify-between">
                <div className="flex justify-between items-start mb-2 gap-2 overflow-hidden">
                    <p className="text-sm font-inter text-[#697079] truncate whitespace-nowrap" title={title}>{title}</p>
                    {icon && <div className="p-2 bg-gray-50 rounded-lg flex-shrink-0">{icon}</div>}
                </div>
                <div className="mt-1">
                    <h3 className="font-semibold text-xl truncate" title={formattedValue}>{formattedValue}</h3>
                </div>
            </div>
            {hasTrend && (
                <div className={`mt-4 flex items-center text-xs font-inter ${trendColor} whitespace-nowrap overflow-hidden`}>
                    <div className="flex items-center truncate">
                        {/* {isPositive ? <TrendingUp size={12} className="mr-1 flex-shrink-0" /> : <TrendingDown size={12} className="mr-1 flex-shrink-0" />} */}
                        <span className="truncate">{formattedTrend}</span>
                    </div>
                    <span className="text-[#1F2937] ml-1 font-normal flex-shrink-0">vs. last month</span>
                </div>
            )}
        </div>
    );
};

export default MarginV2;
